/** Age decisions use the visitor's local calendar date. No birth data is persisted. */
export function ageDecision(year, month, day, today = new Date()) {
  const thisYear = today.getFullYear();
  if (!Number.isInteger(year) || year < 1900 || year > thisYear) return 'invalid';
  if (year < thisYear - 18) return 'adult';
  if (year > thisYear - 18) return 'underage';
  if (month == null || day == null) return 'birthday-required';
  if (!Number.isInteger(month) || !Number.isInteger(day) || month < 1 || month > 12 || day < 1 || day > 31) return 'invalid';
  const birthDate = new Date(year, month - 1, day);
  if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) return 'invalid';
  const birthdayPassed = month < today.getMonth() + 1 || (month === today.getMonth() + 1 && day <= today.getDate());
  return birthdayPassed ? 'adult' : 'underage';
}

if (typeof document !== 'undefined') {
  const visit = window.BozuAgeVisit;
  const consentAttribute = 'Leeftijdscontrole 18+';
  let consentClearPending = null;
  let consentClearSucceeded = false;
  const gate = document.getElementById('bozu-age-gate');
  const form = gate?.querySelector('[data-bozu-age-form]');
  const year = document.getElementById('bozu-age-year');
  const month = document.getElementById('bozu-age-month');
  const day = document.getElementById('bozu-age-day');
  const birthday = gate?.querySelector('[data-bozu-age-birthday]');
  const error = document.getElementById('bozu-age-error');

  function showError(message) {
    error.textContent = message;
    error.hidden = false;
  }

  function updateBirthdayFields() {
    const required = Number(year.value) === new Date().getFullYear() - 18;
    birthday.hidden = !required;
    day.required = required;
    month.required = required;
    error.hidden = true;
  }

  function showGate() {
    if (!gate || !form) return;
    visit?.invalidate();
    form.reset();
    year.max = String(new Date().getFullYear());
    updateBirthdayFields();
    document.documentElement.classList.add('bozu-age-pending');
    // Convert the visible HTML fallback into a native modal, making the page inert.
    if (typeof gate.showModal === 'function' && !gate.matches(':modal')) {
      if (gate.open) gate.close();
      gate.showModal();
    } else if (!gate.open) {
      gate.setAttribute('open', '');
    }
    year.focus({ preventScroll: true });
  }

  function hideGate() {
    form?.reset();
    if (typeof gate?.close === 'function') gate.close();
    else gate?.removeAttribute('open');
    document.documentElement.classList.remove('bozu-age-pending');
  }

  function resetConsents() {
    document.querySelectorAll('bozu-age-consent').forEach(consent => consent.reset?.());
    clearStoredConsent();
  }

  function syncConsents() {
    document.querySelectorAll('bozu-age-consent').forEach(consent => consent.sync?.());
  }

  function clearStoredConsent() {
    // The server rule reads cart attributes, so resetting only the checkbox would
    // leave an earlier checkout confirmation valid on the same cart.
    if (consentClearPending) return consentClearPending;
    consentClearSucceeded = false;
    const root = window.Shopify?.routes?.root || '/';
    consentClearPending = (async () => {
      const current = await fetch(root + 'cart.js', { cache: 'no-store' });
      if (!current.ok) throw new Error('Unable to read age confirmation.');
      const cart = await current.json();
      if (!cart.attributes?.[consentAttribute]) return true;
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [consentAttribute]: '' } }),
      });
      if (!response.ok) throw new Error('Unable to clear age confirmation.');
      const updated = await response.json();
      return !updated.attributes?.[consentAttribute];
    })().catch(() => false).then(succeeded => {
      consentClearSucceeded = succeeded;
      consentClearPending = null;
      syncConsents();
      return succeeded;
    });
    syncConsents();
    return consentClearPending;
  }

  class BozuAgeConsent extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('change', () => {
        this.sync();
        const checkbox = this.querySelector('[data-bozu-age-consent]');
        if (!checkbox?.checked || !consentClearSucceeded) clearStoredConsent();
      }, { signal: this.controller.signal });
      this.reset();
    }

    disconnectedCallback() { this.controller?.abort(); }

    reset() {
      const checkbox = this.querySelector('[data-bozu-age-consent]');
      if (checkbox) checkbox.checked = false;
      this.sync();
      clearStoredConsent();
    }

    sync() {
      const checkbox = this.querySelector('[data-bozu-age-consent]');
      const button = this.querySelector('[name="checkout"]');
      if (button) button.disabled = !checkbox?.checked || this.dataset.cartReady !== 'true' ||
        !consentClearSucceeded || !!consentClearPending;
      const hint = this.querySelector('[data-bozu-consent-hint]');
      if (hint) hint.hidden = !!checkbox?.checked;
      const error = this.querySelector('[data-bozu-consent-error]');
      if (error) {
        const failed = !!checkbox?.checked && !consentClearPending && !consentClearSucceeded;
        error.textContent = failed
          ? 'De leeftijdsbevestiging kon niet worden vernieuwd. Vink de verklaring opnieuw aan om het nogmaals te proberen.'
          : 'Bevestig dat je 18 jaar of ouder bent en kennis hebt genomen van de legitimatiecontrole bij aflevering.';
        error.hidden = !failed;
      }
    }
  }

  if (!customElements.get('bozu-age-consent')) customElements.define('bozu-age-consent', BozuAgeConsent);
  clearStoredConsent();

  if (gate && form) {
    gate.addEventListener('cancel', event => event.preventDefault());
    year.addEventListener('input', updateBirthdayFields);
    gate.querySelector('[data-bozu-underage]').addEventListener('click', () => {
      form.reset();
      updateBirthdayFields();
      showError('Je moet 18 jaar of ouder zijn om deze website te bezoeken.');
      year.focus();
    });
    form.addEventListener('submit', event => {
      event.preventDefault();
      const result = ageDecision(Number(year.value), month.value ? Number(month.value) : null, day.value ? Number(day.value) : null);
      if (result === 'birthday-required') {
        updateBirthdayFields();
        day.focus();
        return;
      }
      if (result !== 'adult') {
        showError(result === 'underage' ? 'Je moet 18 jaar of ouder zijn om deze website te bezoeken.' : 'Vul een geldig geboortejaar en, indien gevraagd, een geldige geboortedatum in.');
        return;
      }
      // The site-entry gate lasts for this visit; the cart declaration remains
      // separate and is never prefilled from this temporary visit flag.
      visit?.verify();
      hideGate();
      document.getElementById('MainContent')?.focus({ preventScroll: true });
    });
    if (visit?.isVerified()) hideGate();
    else showGate();
  }

  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    // Keep internal browsing within the same visit, but never restore cart consent.
    resetConsents();
    document.querySelectorAll('cart-drawer-component dialog[open]').forEach(dialog => dialog.close());
    if (visit?.restore()) hideGate();
    else showGate();
  });

  document.addEventListener('cart:update', resetConsents);

  document.addEventListener('submit', event => {
    const cartForm = event.target;
    if (!(cartForm instanceof HTMLFormElement) || !cartForm.matches('.cart-form')) return;
    const consent = [...document.querySelectorAll('[data-bozu-age-consent]')].find(input => input.form === cartForm);
    const container = consent?.closest('bozu-age-consent');
    if (consent?.checked && container?.dataset.cartReady === 'true' &&
        consentClearSucceeded && !consentClearPending) {
      visit?.markCheckout();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (container) {
      container.querySelector('[data-bozu-consent-error]').hidden = false;
      consent.focus();
    }
  }, true);

  // Storefront links must use the cart declaration, even when an app adds a checkout link.
  document.addEventListener('click', event => {
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor) return;
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkouts?(?:\/|$)/i.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
  }, true);
}
